import React, { useEffect, useState } from "react";
import MasterLayout from "../Sections/MasterLayout";

import {analytics_data ,server_post_json} from "../../ServiceConnection/serviceconnection"
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from "recharts";

const COLORS = {
  primary: "#6366f1",
  secondary: "#8b5cf6",
  success: "#10b981",
  warning: "#f59e0b",
  danger: "#ef4444",
  info: "#3b82f6",
  purple: "#a855f7",
  pink: "#ec4899",
  teal: "#14b8a6",
  orange: "#f97316",
};

const Icon = ({ name, className = "" }) => {
  const icons = {
    dollar: "M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6",
    cart: "M9 2L7.17 6M9 2h6l1.83 4M7.17 6H4.18a1 1 0 0 0-.98 1.22l1.8 9a1 1 0 0 0 .98.78h12.04a1 1 0 0 0 .98-.78l1.8-9A1 1 0 0 0 19.82 6h-2.99M7.17 6h9.66M9 11v6m6-6v6",
    package: "M21 8v13H3V8M12 3v5m0 0L7 5m5 3l5-3M12 21V8",
    users: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm14 10v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75",
    trending: "M23 6l-9.5 9.5-5-5L1 18",
    activity: "M22 12h-4l-3 9L9 3l-3 9H2",
    award: "M12 15a7 7 0 1 0 0-14 7 7 0 0 0 0 14zm0 0v6m-3 3h6",
    map: "M1 6v16l7-4 8 4 7-4V2l-7 4-8-4-7 4z",
    clock: "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zm0 0v10l5 3",
  };
  
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d={icons[name]} />
    </svg>
  );
};

const StatCard = ({ icon, title, value, subtitle, trend, color }) => (
  <div className="bg-white rounded-[10px] shadow-sm border border-gray-200 p-6 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
        <h3 className="text-3xl font-bold text-gray-900 mb-1">{value}</h3>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
      <div className={`p-3 rounded-[10px] bg-${color}-100`}>
        <Icon name={icon} className={`w-6 h-6 text-${color}-600`} />
      </div>
    </div>
    {trend && (
      <div className="mt-4 flex items-center text-sm">
        <Icon name="trending" className="w-4 h-4 text-green-500 mr-1" />
        <span className="text-green-600 font-medium">{trend}</span>
        <span className="text-gray-500 ml-1">vs last period</span>
      </div>
    )}
  </div>
);

const CustomTooltip = ({ active, payload, label, formatValue }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200">
        <p className="text-sm font-semibold text-gray-900 mb-2">{label}</p>
        {payload.map((entry, index) => (
          <div key={index} className="flex items-center justify-between gap-4">
            <span className="text-xs text-gray-600">{entry.name}:</span>
            <span className="text-sm font-bold" style={{ color: entry.color }}>
              {formatValue ? formatValue(entry.value) : entry.value}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export default function Analytics() {
  const [range, setRange] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(false);

  useEffect(() => {
    if (authError) return;
    fetchAnalytics();
  }, [range, authError]);
  
  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await server_post_json(analytics_data, { range });
      setData(res.data.data);
    } catch (error) {
      // 🔥 TOKEN EXPIRED CASE
      if (error === "NO_TOKEN" || error?.response?.status === 401) {
        setAuthError(true);
        return; // 🚫 STOP HERE
      }
  
      // normal error (network etc)
      setData(null);
    } finally {
      setLoading(false);
    }
  };
  

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!data && !authError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 font-medium">Failed to load analytics data</p>
          <button onClick={fetchAnalytics} className="mt-4 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const hasData = data.overview.orders > 0;
  const orderStatusData = Object.entries(data.orderStatus)
    .filter(([_, val]) => val.count > 0)
    .map(([key, val]) => ({
      name: key.replace(/_/g, " ").toUpperCase(),
      value: val.count,
      revenue: val.revenue,
    }));

  const pieColors = [COLORS.primary, COLORS.success, COLORS.warning, COLORS.info, COLORS.purple];

  return (
    <MasterLayout>
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 py-6 px-4">
      <div className="max-w-7xl mx-auto space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
          <h2 className="text-[30px] font-bold text-gray-800">   Analytics Dashboard</h2>
        
            <p className="text-gray-600 text-[15px]">Comprehensive insights into your business performance</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {["today", "7d", "1m", "3m", "6m"].map((r) => (
              <button
                key={r}
                onClick={() => setRange(r)}
                className={`px-5 py-2.5 rounded-[10px] text-[13px] font-semibold transition-all duration-300 ${
                  range === r
                    ? "bg-[var(--primary-color)] text-white scale-105"
                    : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 hover:border-indigo-300"
                }`}
              >
                {r.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {!hasData ? (
          <div className="bg-white rounded-[10px] border border-gray-200 p-12 text-center">
            <Icon name="activity" className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Data Available</h3>
            <p className="text-gray-600">No orders found for the selected time period</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard
                icon="dollar"
                title="Total Revenue"
                value={`₹${data.overview.revenue.toLocaleString()}`}
                subtitle={`Avg: ₹${data.overview.avgOrderValue.toLocaleString()}`}
                color="indigo"
              />
              <StatCard
                icon="cart"
                title="Total Orders"
                value={data.overview.orders}
                subtitle={`${data.conversionFunnel.conversionRate}% conversion`}
                color="green"
              />
              <StatCard
                icon="package"
                title="Items Sold"
                value={data.overview.itemsSold}
                subtitle={`Avg ${(data.overview.itemsSold / data.overview.orders).toFixed(1)} per order`}
                color="purple"
              />
              <StatCard
                icon="users"
                title="Total Customers"
                value={data.customers.total}
                subtitle={`${data.customers.repeatRate}% repeat rate`}
                color="pink"
              />
            </div>

            {data.charts.revenueTimeline.length > 0 && (
              <div className="bg-white rounded-[10px]  border border-gray-200 py-6 px-5 transition-all duration-300">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Revenue Trend</h2>
                    <p className="text-sm text-gray-600 mt-1">Daily revenue and order metrics</p>
                  </div>
                  <Icon name="activity" className="w-6 h-6 text-indigo-600" />
                </div>
                <ResponsiveContainer width="100%" height={350}>
                  <AreaChart data={data.charts.revenueTimeline}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.primary} stopOpacity={0.3} />
                        <stop offset="95%" stopColor={COLORS.primary} stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 12 }}
                      tickFormatter={(val) => new Date(val).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip formatValue={(val) => `₹${val.toLocaleString()}`} />} />
                    <Area type="monotone" dataKey="revenue" stroke={COLORS.primary} strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" name="Revenue" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {orderStatusData.length > 0 && (
                <div className="bg-white rounded-[10px] border border-gray-200 py-6 px-4 transition-all duration-300">
                  <h2 className="text-xl font-bold text-gray-900 mb-6">Order Status Distribution</h2>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={orderStatusData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name.split(" ")[0]} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {orderStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}

              {data.payments.methods.length > 0 && (
                <div className="bg-white rounded-[10px] border border-gray-200 py-6 px-4 hover:shadow-xl transition-all duration-300">
                  <h2 className="text-xl font-bold text-gray-900 mb-6">Payment Methods</h2>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={data.payments.methods}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="method" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip content={<CustomTooltip formatValue={(val) => `₹${val.toLocaleString()}`} />} />
                      <Legend />
                      <Bar dataKey="revenue" fill={COLORS.success} name="Revenue" radius={[8, 8, 0, 0]} />
                      <Bar dataKey="orders" fill={COLORS.info} name="Orders" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {data.products.topSelling.length > 0 && (
              <div className="bg-white rounded-[10px] border border-gray-200 py-6 px-5 transition-all duration-300">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Top Selling Products</h2>
                    <p className="text-sm text-gray-600 mt-1">Best performers by quantity and revenue</p>
                  </div>
                  <Icon name="award" className="w-6 h-6 text-yellow-500" />
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-gray-200 bg-gray-50">
                        <th className="text-left py-4 px-4 text-sm font-bold text-gray-700">Product</th>
                        <th className="text-right py-4 px-4 text-sm font-bold text-gray-700">Quantity</th>
                        <th className="text-right py-4 px-4 text-sm font-bold text-gray-700">Revenue</th>
                        <th className="text-right py-4 px-4 text-sm font-bold text-gray-700">Orders</th>
                        <th className="text-right py-4 px-4 text-sm font-bold text-gray-700">Avg Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.products.topSelling.map((product, idx) => (
                        <tr key={product.product_id} className="border-b border-gray-100 hover:bg-indigo-50 transition-all duration-200 cursor-pointer group">
                          <td className="py-4 px-4">
                            <div className="flex items-center">
                              <div className="w-10 h-10 rounded-[10px] bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center mr-3 text-white font-bold shadow-md group-hover:scale-110 transition-transform duration-300">
                                {idx + 1}
                              </div>
                              <div>
                                <p className="font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors">{product.title || 'Unknown Product'}</p>
                                <p className="text-xs text-gray-500">{product.product_id}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-4 text-right">
                            <span className="inline-flex items-center px-4 py-2 rounded-full bg-purple-100 text-purple-700 font-bold text-sm">
                              {product.quantity}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-right font-bold text-gray-900">₹{product.revenue.toLocaleString()}</td>
                          <td className="py-4 px-4 text-right text-gray-600 font-medium">{product.orders}</td>
                          <td className="py-4 px-4 text-right text-gray-600 font-medium">₹{Math.round(product.avgPrice)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {data.customers.topCustomers.length > 0 && (
              <div className="bg-white rounded-[10px]  border border-gray-200 py-6 px-5 transition-all duration-300">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Top Customers</h2>
                    <p className="text-sm text-gray-600 mt-1">Your most valuable customers</p>
                  </div>
                  <Icon name="users" className="w-6 h-6 text-pink-500" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {data.customers.topCustomers.map((customer, idx) => (
                    <div key={customer.user_id} className="border border-gray-200 rounded-[10px] p-6 hover:shadow-lg hover:border-indigo-400 transition-all duration-300 cursor-pointer transform hover:-translate-y-1">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-lg mr-3 shadow-md">
                            #{idx + 1}
                          </div>
                          <div>
                            <p className="font-bold text-gray-900">{customer.user_id}</p>
                            <p className="text-xs text-gray-500 font-medium">{customer.orders} orders</p>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center p-2 bg-green-50 rounded-lg">
                          <span className="text-sm font-medium text-gray-700">Total Spent:</span>
                          <span className="font-bold text-green-600">₹{customer.spent.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Items:</span>
                          <span className="font-semibold text-gray-900">{customer.itemsPurchased}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Avg Order:</span>
                          <span className="font-semibold text-gray-900">₹{customer.avgOrderValue.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(data.geographic.byState.length > 0 || data.geographic.byCity.length > 0) && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {data.geographic.byState.length > 0 && (
                  <div className="bg-white rounded-[10px] border border-gray-200 py-6 px-5  transition-all duration-300">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900">Top States</h2>
                        <p className="text-sm text-gray-600 mt-1">Sales by state</p>
                      </div>
                      <Icon name="map" className="w-6 h-6 text-teal-500" />
                    </div>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={data.geographic.byState} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis type="number" tick={{ fontSize: 12 }} />
                        <YAxis dataKey="state" type="category" tick={{ fontSize: 12 }} width={100} />
                        <Tooltip content={<CustomTooltip formatValue={(val) => `₹${val.toLocaleString()}`} />} />
                        <Bar dataKey="revenue" fill={COLORS.teal} radius={[0, 8, 8, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {data.geographic.byCity.length > 0 && (
                  <div className="bg-white rounded-[10px] border border-gray-200 py-6 px-5 transition-all duration-300">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h2 className="text-2xl font-bold text-gray-900">Top Cities</h2>
                        <p className="text-sm text-gray-600 mt-1">Sales by city</p>
                      </div>
                      <Icon name="map" className="w-6 h-6 text-orange-500" />
                    </div>
                    <div className="space-y-3 max-h-80 overflow-y-auto">
                      {data.geographic.byCity.map((city, idx) => (
                        <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-indigo-50 transition-all duration-200 cursor-pointer">
                          <div className="flex items-center">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center text-white font-bold text-sm mr-3">
                              {idx + 1}
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{city.city}</p>
                              <p className="text-xs text-gray-500">{city.state}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-gray-900">₹{city.revenue.toLocaleString()}</p>
                            <p className="text-xs text-gray-500">{city.orders} orders</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {range === "today" && data.charts.hourlyData.length > 0 && (
              <div className="bg-white rounded-[10px] border border-gray-200 py-6 px-5 hover:shadow-lg transition-all duration-300">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Hourly Activity</h2>
                    <p className="text-sm text-gray-600 mt-1">Today's order distribution by hour</p>
                  </div>
                  <Icon name="clock" className="w-6 h-6 text-orange-500" />
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={data.charts.hourlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="hour" tick={{ fontSize: 12 }} tickFormatter={(val) => `${val}:00`} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line type="monotone" dataKey="orders" stroke={COLORS.orange} strokeWidth={3} name="Orders" dot={{ r: 4 }} />
                    <Line type="monotone" dataKey="revenue" stroke={COLORS.success} strokeWidth={3} name="Revenue" dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        )}
      </div>
    </div>
    </MasterLayout>
  );
}